import { Button, DatePicker, Flex, Form, Input, InputNumber, Modal, ModalProps, Space, Spin, Divider, Tag, Select, DescriptionsProps, Descriptions, notification } from "antd";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { ChangeEnum, ConfirmedEnum, OperateEnum } from "@/types/inventory";
import { PaginationParams, SourceTypeEnum } from "@/types/common";
import { Product } from "@/types/product";
import { InventoryContent } from "./style";
import dayjs from "dayjs";
import { PurchaseOrder, PurchaseOrderItem } from "@/types/purchase";
import PrintInventoryV2, { PrintType } from "@/SupplierPages/Home/InventoryOrder/components/PrintInventoryV2";
import { DictContext } from "@/layout/context/DictContext";
import { OrderAddItemV2, OrderAddV2, OrderItemV2 } from "@/types/inventoryV2";
import { purOrderInventories } from "@/apis/purchase";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { CountrySelect } from "@/components/Selector";
import dictApi from "@/apis/dict";
import { generateLotV2 } from "@/utils/codeParse";


interface InventoryFormProps extends ModalProps {
  handleCancel: () => void;
  handleOk: (data: OrderAddV2) => void;
  changeType: ChangeEnum;
  handleSaveDraft?: (data: OrderAddV2) => Promise<void>;
  fetchApi: <Params extends unknown[]>(
    ...args: Params
  ) => Promise<{
    body: Product[];
    pagination: PaginationParams | undefined;
  }>;
  formTitle: string;
  value: Product | null;
  id?: string | undefined;
  sourceId: string | null;
  loading: boolean;
  purchaseOrder: PurchaseOrder;
  purchaseOrderItem: PurchaseOrderItem;
  detail: PurchaseOrderItem;
}

const InventoryForm: FC<InventoryFormProps> = ({
  handleOk,
  handleCancel,
  fetchApi,
  formTitle,
  changeType,
  value,
  id,
  sourceId,
  loading,
  purchaseOrder,
  purchaseOrderItem,
  detail,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const PRODUCT_NUM_MIN = 1;
  const [inventoryOrdersList, setInventoryOrderLIst] = useState<Array<OrderItemV2>>([])
  const [modalForm] = Form.useForm();
  const [printingId, setPrintingId] = useState<string>('')
  const { printSetting, brandData, setBrandData } = useContext(DictContext);

  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [showExtFields, setShowExtFields] = useState<boolean>(false);

  const handleSubmit = debounce(() => {
    modalForm.submit();
  }, 300);

  useEffect(() => {
    // if (detail?.inventoryItems && detail.inventoryItems.length > 0) setInventoryOrderLIst([...detail.inventoryItems])
    // else {
    setLoadingData(true)
    purOrderInventories(purchaseOrderItem.id)
      .then(res => {
        setInventoryOrderLIst([...res])
      })
      .catch(() => { })
      .finally(() => setLoadingData(false))

    // }
    // 初始化表单值
    const setInitialValues = () => {
      modalForm.setFieldsValue({
        productName: value?.name,
        originalBrand: detail?.brand ? [detail.brand] : undefined,
        originalCode: detail?.mpn ?? undefined,
        dcc: detail?.dcc ?? undefined,
        netWeightDecimal: detail?.netWeight ?? undefined,
        grossWeightDecimal: detail?.grossWeight ?? undefined,
      });
    };
    // 品牌字典缺少采购明细品牌时，先补字典，确保多选下拉可以正常回显。
    if (detail?.brand && !brandData.some(item => item.desc === detail.brand)) {
      dictApi.add(DictEnum.BRAND, { key: `${Date.now()}`, desc: detail.brand })
        .then(res => {
          setBrandData([...brandData, res]);
          setInitialValues();
        })
        .catch(() => {
          setInitialValues();
        });
    } else {
      setInitialValues();
    }
    handleGenLot();
  }, [])

  const { maxQuantity } = useMemo(() => {
    if (detail) {
      let maxQuantity = 0

      let max = Number(detail.count || 0);
      max = max + Number(detail.spareCount || 0);
      let curQuantity: number = 0;

      if (inventoryOrdersList && inventoryOrdersList.length > 0) {
        inventoryOrdersList.forEach(o => {

          if (o?.order?.opType === OperateEnum.TRANSACTION) {  //正常采购入库
            curQuantity += Number(o.count)
          } else {    //退库
            if (o?.order?.confirmState === ConfirmedEnum.CONFIRM) { //确认的单据：出库- 入库+
              if (o?.order?.type === ChangeEnum.IN) curQuantity += Number(o.count)
              else curQuantity -= Number(o.count)
            } else {//未确认的单据： 入库+ 出库不管
              if (o?.order?.type === ChangeEnum.IN) curQuantity += Number(o.count)
            }
          }
        });
      }

      maxQuantity = Math.max(Number(max) - curQuantity, 0)
      modalForm?.setFieldValue("quantity", maxQuantity);
      return {
        maxQuantity
      }
    }
    return { lotStr: '', maxQuantity: 0 };
  }, [inventoryOrdersList, detail]);

  const handleGenLot = () => {
    let lotStr = generateLotV2(purchaseOrderItem?.product?.code ?? '', purchaseOrder?.supplier?.code ?? '', Date.now());
    if (!!!lotStr) {
      notification.error({ message: t('common.lotGetErr'), description: `${t('purchase.printCode')}：${purchaseOrderItem?.product?.code ?? ''}，${t('purchase.code')}：${purchaseOrder?.supplier?.code ?? ''}` })
      return
    }
    modalForm.setFieldValue("lot", lotStr);
  };

  const onFinish = (res: {
    quantity: number;
    thdlot?: string;
    lot: string;
    arrivalAt?: number;
    originalBrand?: string[];
    originalCode?: string;
    dcc?: string;
    manufactureDate?: number;
    msdLevel?: string;
    originPlace?: string;
    netWeightDecimal?: number | null;
    grossWeightDecimal?: number | null;
    desc?: string;
  }) => {

    const hasExtValue = res.originalBrand || res.originalCode || res.dcc || res.manufactureDate || res.msdLevel || res.originPlace ||
      res.netWeightDecimal != null || res.grossWeightDecimal != null;

    const ext = hasExtValue ? {
      originalBrand: res.originalBrand?.join(","),
      originalCode: res.originalCode ?? '',
      manufactureDate: res.manufactureDate ? dayjs(res.manufactureDate)?.endOf("day")?.valueOf() : undefined,
      msdLevel: res.msdLevel ?? '',
      originPlace: res.originPlace ?? '',
      netWeightDecimal: res.netWeightDecimal ?? undefined,
      grossWeightDecimal: res.grossWeightDecimal ?? undefined,
      dcc: res.dcc ?? '',
    } : undefined;

    // let lotStr = generateLotV2(purchaseOrderItem?.product?.code ?? '', purchaseOrder?.supplier?.code ?? '', Date.now());
    // if (!!!lotStr) {
    //   notification.error({ message: t('common.lotGetErr'), description: `${t('purchase.printCode')}：${purchaseOrderItem?.product?.code ?? ''}，${t('purchase.code')}：${purchaseOrder?.supplier?.code ?? ''}` })
    //   return
    // }
    const newProducts: Array<OrderAddItemV2> = [
      {
        sourceType: SourceTypeEnum.PURCHASE_ITEM,
        sourceId: detail.id,
        productId: value?.id ?? '',
        count: res.quantity,
        lot: res.lot,
        thdlot: res?.thdlot ?? '',
        desc: '',
        locations: [],
        ext,
      }
    ];
    const arrivalAt = res.arrivalAt ? dayjs(res.arrivalAt).endOf('day').valueOf() : null
    handleOk({
      items: newProducts,
      type: changeType,
      opType: OperateEnum.TRANSACTION,
      desc: res?.desc ?? '',
      preTime: arrivalAt,
      arrivalAt: null,
    })
  }
  const onClear = () => {
    modalForm.resetFields();
    handleCancel();
  };
  const descItems: DescriptionsProps["items"] = [
    {
      key: "productName",
      label: t('purchase.goodName'),
      children: value?.name,
      span: 1.5
    },
    {
      key: "productCode",
      label: t('purchase.goodCode'),
      children: value?.code,
      span: 1.5
    },
    {
      key: "count",
      label: t('purchase.purCount'),
      children: detail?.count
    },

    {
      key: "spareRate",
      label: t('purchase.spareRate'),
      children: detail?.spareRate
    },
    {
      key: "spareCount",
      label: t('purchase.spareCount'),
      children: detail?.spareCount
    },
  ]

  return (
    <Modal width={600}
      onCancel={onClear}
      maskClosable={false}
      footer={null}
      title={formTitle}
      {...modalProps}
    >
      <Spin spinning={loadingData}>
        <Form form={modalForm} name="modalForm" autoComplete="off" onFinish={onFinish}>
          <Form.Item name="arrivalAt" label={t("purchase.deliveryDate")} style={{ marginBottom: "6px" }}
            rules={[{ required: true, message: t("purchase.selectDeliveryDate") },]} initialValue={dayjs()}
          >
            <DatePicker placeholder={t("purchase.selectDeliveryDate")} style={{ width: 180 }} />
          </Form.Item>
          <Descriptions items={descItems} column={3} />
          <Flex
            align="center"
            justify="space-between"
            style={{ paddingTop: 6, paddingBottom: 6 }}
            gap={10}
          >
            {value && <Form.Item
              noStyle
              name={'productName'}
            >
              <Input disabled={true} />
            </Form.Item>}

            <Form.Item
              noStyle
              name={'quantity'}
              // initialValue={maxQuantity}
              rules={[
                { required: true, message: t("warehouse.plProductNum") },
                {
                  min: PRODUCT_NUM_MIN,
                  message: t("warehouse.productNumMin", {
                    num: PRODUCT_NUM_MIN,
                  }),
                  type: "number",
                },
              ]}
            >
              <InputNumber changeOnWheel
                style={{ width: 200 }}
                placeholder={t("warehouse.plProductNum")}
                precision={0}
                min={1}
                max={maxQuantity}
              />
            </Form.Item>
          </Flex>
          <Form.Item name='lot' label={'lot'}
            style={{ marginBottom: 6 }}
            rules={[{ required: true, message: 'lot' },]}>
            <Input placeholder={'lot'} suffix={
              <Button onClick={() => handleGenLot()} >{t('common.genLot')}</Button>
            } />
          </Form.Item>
          <Form.Item name='thdlot' label={t("purchase.supplierBatchNo")} style={{ marginBottom: 6 }}>
            <Input placeholder={t("purchase.plSupplierBatchNo")} />
          </Form.Item>
          <Form.Item name='desc' label={t("requirement.inOrderDesc")} style={{ marginBottom: 6 }}>
            <Input.TextArea placeholder={t("requirement.plInventoryDesc")} />
          </Form.Item>
          {showExtFields && (
            <>
              <Divider style={{ marginBottom: 6, marginTop: 0 }} >{t('purchase.otherInfo')}</Divider>
              <Form.Item name='originalBrand' label={t("purchase.originalBrand")} style={{ marginBottom: 6 }}>
                {/* <Input placeholder={t("purchase.plOriginalBrand")} /> */}
                <Dict type={DictEnum.BRAND} mode="multiple" />
              </Form.Item>
              <Form.Item name='originalCode' label={t("purchase.originalCode")} style={{ marginBottom: 6 }}>
                <Input placeholder={t("purchase.plOriginalCode")} />
              </Form.Item>
              <Form.Item name='dcc' label={t("purchase.dcc")} style={{ marginBottom: 6 }}>
                <Input placeholder={t("purchase.plShelfLife")} />
              </Form.Item>
              <Form.Item name='manufactureDate' label={t("purchase.manufactureDate")} style={{ marginBottom: 6 }}>
                {/* <InputNumber changeOnWheel placeholder={t("purchase.plManufactureDate")} style={{ width: '100%' }} /> */}
                <DatePicker placeholder={t("purchase.plManufactureDate")} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name='msdLevel' label={t("purchase.msdLevel")} style={{ marginBottom: 6 }}>
                <Select placeholder={t("purchase.plMsdLevel")} allowClear options={[
                  { label: 'MSL 1', value: 'MSL 1' },
                  { label: 'MSL 2', value: 'MSL 2' },
                  { label: 'MSL 2a', value: 'MSL 2a' },
                  { label: 'MSL 3', value: 'MSL 3' },
                  { label: 'MSL 4', value: 'MSL 4' },
                  { label: 'MSL 5', value: 'MSL 5' },
                  { label: 'MSL 5a', value: 'MSL 5a' },
                  { label: 'MSL 6', value: 'MSL 6' },
                ]} />
              </Form.Item>
              <Form.Item name='originPlace' label={t("purchase.originPlace")} style={{ marginBottom: 6 }}>
                {/* <Input placeholder={t("purchase.plOriginPlace")} /> */}
                <CountrySelect placeholder={t("purchase.plOriginPlace")} />
              </Form.Item>
              <Form.Item name='netWeightDecimal' label={t("purchase.netWeight")} style={{ marginBottom: 6 }}>
                <InputNumber changeOnWheel min={0} placeholder={t("purchase.plNetWeight")} style={{ width: '100%' }} addonAfter="kg" />
              </Form.Item>
              <Form.Item name='grossWeightDecimal' label={t("purchase.grossWeight")} style={{ marginBottom: 6 }}>
                <InputNumber changeOnWheel min={0} placeholder={t("purchase.plGrossWeight")} style={{ width: '100%' }} addonAfter="kg" />
              </Form.Item>
            </>
          )}
          <Flex justify="space-between" style={{ paddingBottom: 6 }} >
            <Button type="link" onClick={() => setShowExtFields(!showExtFields)}>
              {showExtFields ? t('common.collapse') : t('common.expand')}
            </Button>
            <Space>
              <Button type="link" danger onClick={onClear}>{t('requirement.close')}</Button>
              <Button disabled={loading} loading={loading} type="primary" onClick={handleSubmit}>{t('common.confirm')}</Button>
            </Space>
          </Flex>
          <Flex justify="space-between" align="baseline" >
            <h3>{t('purchase.deliveryOrders')}：</h3>
            <span>{t('purchase.orderNums')}: {inventoryOrdersList.length}</span>
          </Flex>
        </Form>
        <InventoryContent>
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {inventoryOrdersList.map((item) => {
              return (
                <div style={{ border: "1px solid #c6bfbf", borderRadius: 10, padding: '15px 15px', marginBottom: 10 }}>
                  <span>{t('purchase.deliveryNumber')}:{item?.order?.number ?? ''}</span>
                  {/* 退库 */}
                  {item?.order?.opType !== OperateEnum.TRANSACTION && <span style={{ marginLeft: 50 }}>
                    {t('purchase.state') + '：'}{
                      item?.order?.confirmState === ConfirmedEnum.CONFIRM ?
                        <Tag color="red">
                          {t('warehouse.returnStock')}({item?.order?.type === ChangeEnum.OUT ? t('purchase.isOut') : t('purchase.inStock')})
                        </Tag>
                        : <Tag color={"orange"}>{t('purchase.toReturn')}({item?.order?.type === ChangeEnum.OUT ? t('inventory.outbound') : t('inventory.inbound')})</Tag>
                    }
                  </span>}
                  {/* 交易 */}
                  {item?.order?.opType === OperateEnum.TRANSACTION && <span style={{ marginLeft: 50 }}>
                    {t('purchase.state') + '：'}{
                      item?.order?.confirmState === ConfirmedEnum.CONFIRM ?
                        <Tag color={'blue'}>{t('purchase.inStock')}</Tag>
                        : <Tag color={"warning"}>{t('purchase.notInStock')}</Tag>
                    }
                  </span>}
                  <Divider style={{ margin: '5px 0' }} />
                  <Form labelCol={{ span: 4 }}>
                    <Form.Item label={t('purchase.goodName')} name={'typeName'} initialValue={value?.name}>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label={t('purchase.goodCode')} name={'typeCode'} initialValue={value?.code || ''}>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label={t('purchase.goodModel')} name={'type'} initialValue={value?.model || ''}>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label={t('purchase.quantity')} name={'num'} initialValue={item.count}>
                      <Input disabled />
                    </Form.Item>
                  </Form>
                  <Flex justify="end">
                    <PrintInventoryV2 orderMark={detail?.order?.remark ?? ''} itemMark={detail?.remark ?? ''} showHeader
                      dictAddress={printSetting.find(ps => ps.key === "companyLocation")?.value} onStart={() => setPrintingId(item.id)} onEnd={() => setPrintingId('')}
                      selectedRows={[item]} disabled={!!printingId && printingId !== item.id} showBottom
                      purOrder={{ ...detail.order, product: detail.product }} purOrderNo={detail.number} printType={PrintType.PURCHASE}
                    />
                  </Flex>
                </div>
              )
            })}
          </div>
        </InventoryContent>
      </Spin>
    </Modal >
  );
};

export default InventoryForm;
